import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, X, Loader2, ChefHat, Sparkles, Trash2, Receipt, Camera, CheckCircle, ShoppingCart, TrendingDown, Lock, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { useAppStore } from '@/hooks/useAppStore';
import { useChefGPT, ChefGPTRecipe } from '@/hooks/useChefGPT';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { ROUTE_PATHS } from '@/lib/index';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';

const EDGE_FUNCTION_URL = 'https://pgjncthsfxsylmvkgibx.supabase.co/functions/v1/meal-plan';
const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBnam5jdGhzZnhzeWxtdmtnaWJ4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxMDEwMTEsImV4cCI6MjA5MjY3NzAxMX0.PDtIhTOuZNnwOqNkdOD8eTC6SCXdlPT5H7vrcU30UXk';

const SUGGESTIONS = [
  'olive oil','butter','flour','sugar','salt','black pepper','garlic',
  'onion','eggs','milk','rice','pasta','canned tomatoes','chicken broth',
  'soy sauce','honey','cumin','paprika','oregano','tomato paste',
  'baking soda','cornstarch','vinegar','hot sauce','mustard',
];

const CAT_EMOJI: Record<string,string> = {
  meat:'🥩', produce:'🥦', dairy:'🧀', pantry:'🫙', bakery:'🍞', frozen:'🧊', other:'🛒'
};

interface ScannedItem { name: string; qty: string; price: number; category: string; }
interface ReceiptScan { store_name: string; total_spent: number; items: ScannedItem[]; scanned_at?: string; }

const STORAGE_KEY_RECEIPTS = 'flyerfeast-receipts';

function loadReceipts(): ReceiptScan[] {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY_RECEIPTS) ?? '[]'); } catch { return []; }
}
function saveReceipts(r: ReceiptScan[]) {
  localStorage.setItem(STORAGE_KEY_RECEIPTS, JSON.stringify(r.slice(0, 20)));
}

// Weekly total from receipts scanned in last 7 days
function weeklyTotal(receipts: ReceiptScan[]): number {
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return receipts
    .filter(r => new Date(r.scanned_at ?? 0).getTime() > cutoff)
    .reduce((s, r) => s + (r.total_spent ?? 0), 0);
}

export default function MyPantryPage() {
  const navigate = useNavigate();
  const { postalCode, selectedStore, setMealPlan } = useAppStore();
  const { user, setShowAuthModal } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { fetchRecipeFromIngredients } = useChefGPT();

  // ChefGPT Quick Recipe state
  const [cgLoading, setCgLoading]     = useState(false);
  const [cgRecipe, setCgRecipe]       = useState<ChefGPTRecipe | null>(null);
  const [cgError, setCgError]         = useState('');
  const [cgOpen, setCgOpen]           = useState(false);
  const [cgStepsOpen, setCgStepsOpen] = useState(false);

  const fetchChefGPTRecipe = async () => {
    if (items.length < 2) { setCgError('Add at least 2 pantry items first.'); return; }
    setCgLoading(true); setCgError(''); setCgRecipe(null); setCgOpen(false);
    try {
      const recipe = await fetchRecipeFromIngredients(items.slice(0, 12), 4);
      setCgRecipe(recipe);
      setCgOpen(true);
    } catch (e) {
      setCgError((e as Error).message?.slice(0, 120) ?? 'ChefGPT could not generate a recipe.');
    } finally {
      setCgLoading(false);
    }
  };

  const [tab, setTab]             = useState<'pantry' | 'receipts'>('pantry');
  const [items, setItems]         = useState<string[]>([]);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [generated, setGenerated] = useState(false);

  // Receipt state
  const [receipts, setReceipts]           = useState<ReceiptScan[]>([]);
  const [scanning, setScanning]           = useState(false);
  const [scanPreview, setScanPreview]     = useState<string | null>(null);
  const [pendingScan, setPendingScan]     = useState<ReceiptScan | null>(null);
  const [scanError, setScanError]         = useState('');
  const [scanSuccess, setScanSuccess]     = useState(false);

  // Load pantry — Supabase when logged in, localStorage fallback
  useEffect(() => {
    if (user) {
      // Load pantry
      supabase
        .from('user_pantry')
        .select('items')
        .eq('user_id', user.id)
        .single()
        .then(({ data }) => {
          if (data?.items && Array.isArray(data.items) && data.items.length > 0) {
            setItems(data.items as string[]);
          } else {
            const saved = localStorage.getItem('flyerfeast-pantry');
            if (saved) setItems(JSON.parse(saved));
          }
        });
      // Load receipts from Supabase
      supabase
        .from('receipt_scans')
        .select('store_name, total_spent, items, scanned_at')
        .eq('user_id', user.id)
        .order('scanned_at', { ascending: false })
        .limit(20)
        .then(({ data }) => {
          if (data && data.length > 0) {
            const remote: ReceiptScan[] = data.map(r => ({
              store_name:  r.store_name ?? '',
              total_spent: Number(r.total_spent ?? 0),
              items:       Array.isArray(r.items) ? r.items : [],
              scanned_at:  r.scanned_at ?? new Date().toISOString(),
            }));
            setReceipts(remote);
            saveReceipts(remote); // mirror to localStorage
          } else {
            setReceipts(loadReceipts());
          }
        });
    } else {
      const saved = localStorage.getItem('flyerfeast-pantry');
      if (saved) setItems(JSON.parse(saved));
      setReceipts(loadReceipts());
    }
  }, [user?.id]);

  const saveItems = (next: string[]) => {
    setItems(next);
    localStorage.setItem('flyerfeast-pantry', JSON.stringify(next));
    // Sync to Supabase if logged in
    if (user) {
      supabase
        .from('user_pantry')
        .upsert({ user_id: user.id, items: next, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
        .then(({ error }) => { if (error) console.error('[FF] save pantry:', error.message); });
    }
  };

  const addItem = (val?: string) => {
    const v = (val ?? input).trim().toLowerCase();
    if (!v || items.includes(v)) { setInput(''); return; }
    saveItems([...items, v]);
    setInput('');
  };

  const removeItem = (i: number) => saveItems(items.filter((_, idx) => idx !== i));
  const clearAll   = () => saveItems([]);

  const generateMeals = async () => {
    if (items.length < 3) { setError('Add at least 3 pantry items to generate meals.'); return; }
    setError(''); setLoading(true); setGenerated(false);
    try {
      const res = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON_KEY}`, 'apikey': ANON_KEY },
        body: JSON.stringify({ postalCode: postalCode || 'M5V3A8', storeName: selectedStore?.name ?? '', merchant: '', pantryMode: true, pantryItems: items }),
      });
      const ct = res.headers.get('content-type') ?? '';
      if (!ct.includes('application/json')) { const t = await res.text(); throw new Error(`Server error: ${t.slice(0,120)}`); }
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error ?? `Error ${res.status}`);
      setMealPlan(data); setGenerated(true);
      setTimeout(() => navigate(ROUTE_PATHS.MEAL_PLAN), 800);
    } catch (e) { setError((e as Error).message?.slice(0,120) ?? 'Something went wrong.'); }
    finally { setLoading(false); }
  };

  // ── Receipt scanning ──────────────────────────────────────────────────────
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setScanError(''); setPendingScan(null); setScanSuccess(false);

    // Compress image before sending (phone photos can be 4MB+)
    const compressImage = (file: File): Promise<{base64: string; mime: string; dataUrl: string}> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const MAX = 800;
          const scale = Math.min(1, MAX / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width  = Math.round(img.width  * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          if (!ctx) { reject(new Error('Canvas not supported')); return; }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          resolve({ base64: dataUrl.split(',')[1], mime: 'image/jpeg', dataUrl });
        };
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = url;
      });

    const reader = new FileReader();
    reader.onload = async () => {
      setScanning(true);
      try {
        const { base64, mime, dataUrl } = await compressImage(file);
        setScanPreview(dataUrl);
        const res = await fetch(EDGE_FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${ANON_KEY}`, 'apikey': ANON_KEY },
          body: JSON.stringify({ action: 'scan_receipt', postalCode: postalCode || 'M5V3A8', imageBase64: base64, imageMime: mime }),
        });
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error ?? 'Scan failed');
        setPendingScan({ ...data, scanned_at: new Date().toISOString() });
      } catch (e) {
        const raw = (e as Error).message ?? '';
        // Translate technical AI errors into friendly messages
        let friendly = 'Could not read receipt. Try a clearer photo in good lighting.';
        if (raw.includes('no vision') || raw.includes('vision') || raw.includes('gemma') || raw.includes('gemini')) {
          friendly = 'Photo scan unavailable right now. Try again in a moment, or enter items manually.';
        } else if (raw.includes('network') || raw.includes('fetch') || raw.includes('Failed to fetch')) {
          friendly = 'Network error — check your connection and try again.';
        } else if (raw.includes('too large') || raw.includes('size')) {
          friendly = 'Photo is too large. Try a lower-resolution shot.';
        } else if (raw.length > 0 && raw.length < 80 && !raw.includes(':')) {
          friendly = raw; // short human-readable error, show as-is
        }
        setScanError(friendly);
        setScanPreview(null);
      } finally { setScanning(false); }
    };
    reader.readAsDataURL(file);
  };

  const confirmScan = async () => {
    if (!pendingScan) return;
    const scan: ReceiptScan = { ...pendingScan, scanned_at: new Date().toISOString() };
    const updated = [scan, ...receipts];
    setReceipts(updated);
    saveReceipts(updated);
    // Add receipt items to pantry
    const newItems = scan.items
      .map(i => i.name.toLowerCase().trim())
      .filter(n => n && !items.includes(n));
    if (newItems.length > 0) saveItems([...items, ...newItems]);
    // Save to Supabase if logged in
    if (user) {
      supabase.from('receipt_scans').insert({
        id:          crypto.randomUUID(),
        user_id:     user.id,
        store_name:  scan.store_name,
        total_spent: scan.total_spent,
        items:       scan.items,
        scanned_at:  scan.scanned_at ?? new Date().toISOString(),
      }).then(({ error }) => { if (error) console.error('[FF] save receipt:', error.message); });
    }
    setPendingScan(null); setScanPreview(null); setScanSuccess(true);
    setTimeout(() => setScanSuccess(false), 3000);
  };

  const discardScan = () => { setPendingScan(null); setScanPreview(null); setScanError(''); };
  const deleteReceipt = (idx: number) => {
    const updated = receipts.filter((_, i) => i !== idx);
    setReceipts(updated); saveReceipts(updated);
  };

  const weekly = weeklyTotal(receipts);
  const suggestions = SUGGESTIONS.filter(s => !items.includes(s));

    // ── Auth gate ──────────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-12">
        <header className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border/50">
          <div className="container mx-auto px-4 h-14 flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <ChefHat className="w-5 h-5 text-primary" />
            <span className="font-bold text-foreground flex-1">My Pantry & Receipts</span>
          </div>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center gap-5 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground mb-2">Sign in to use My Pantry</h2>
            <p className="text-muted-foreground text-sm max-w-xs">
              Save your pantry items, scan receipts, and track your weekly spending — all synced to your account.
            </p>
          </div>
          <button
            onClick={() => setShowAuthModal(true)}
            className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:opacity-90 transition-opacity"
          >
            Sign In / Create Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-12 pb-24">
      {/* Header */}
      <header className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="container mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <ChefHat className="w-5 h-5 text-primary" />
          <span className="font-bold text-foreground flex-1">My Pantry & Receipts</span>
          {tab === 'pantry' && items.length > 0 && (
            <button onClick={clearAll} className="text-xs text-muted-foreground hover:text-destructive font-medium flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Clear
            </button>
          )}
          {tab === 'pantry' && items.length >= 2 && (
            <button onClick={fetchChefGPTRecipe} disabled={cgLoading}
              className="text-xs font-bold text-primary border border-primary/30 bg-primary/8 hover:bg-primary/15 px-2.5 py-1 rounded-full flex items-center gap-1 transition-colors disabled:opacity-50">
              {cgLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              Quick Recipe
            </button>
          )}
        </div>
        {/* Tabs */}
        <div className="flex border-b border-border/50">
          <button onClick={() => setTab('pantry')}
            className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab==='pantry' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}>
            <ChefHat className="w-4 h-4" /> Pantry {items.length > 0 && <span className="text-xs bg-primary/15 text-primary rounded-full px-1.5">{items.length}</span>}
          </button>
          <button onClick={() => setTab('receipts')}
            className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab==='receipts' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}>
            <Receipt className="w-4 h-4" /> Receipts {receipts.length > 0 && <span className="text-xs bg-primary/15 text-primary rounded-full px-1.5">{receipts.length}</span>}
          </button>
        </div>
      </header>

      <div className="container mx-auto max-w-xl px-4 py-5 space-y-5">

        {/* ── PANTRY TAB ── */}
        <AnimatePresence mode="wait">
        {tab === 'pantry' && (
          <motion.div key="pantry" initial={{opacity:0,x:-10}} animate={{opacity:1,x:0}} exit={{opacity:0,x:10}} className="space-y-5">

            <div className="bg-primary/8 border border-primary/20 rounded-2xl p-4">
              <p className="text-sm font-bold text-foreground mb-1">🥫 What's in your pantry?</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Add ingredients you already have. The AI builds meals using what you've got + this week's sale items.
                <span className="text-primary font-medium"> Scan a receipt to auto-stock your pantry.</span>
              </p>
            </div>

            <div className="flex gap-2">
              <input type="text" value={input}
                onChange={e => { setInput(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && addItem()}
                placeholder="e.g. olive oil, garlic, pasta…"
                className="flex-1 h-12 px-4 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button onClick={() => addItem()} disabled={!input.trim()}
                className="h-12 px-4 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center gap-1.5 hover:opacity-90 disabled:opacity-40 transition-opacity">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>

            {suggestions.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Quick add</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.slice(0,16).map(s => (
                    <button key={s} onClick={() => addItem(s)}
                      className="text-xs bg-muted hover:bg-primary/10 hover:text-primary border border-border hover:border-primary/30 text-muted-foreground font-medium px-3 py-1.5 rounded-full transition-colors">
                      + {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {items.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Your pantry ({items.length} item{items.length !== 1 ? 's' : ''})
                </p>
                <motion.div variants={staggerContainer} initial="hidden" animate="visible"
                  className="bg-card border border-border rounded-2xl overflow-hidden">
                  {items.map((item, i) => (
                    <motion.div key={item} variants={staggerItem}
                      className="flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0">
                      <span className="text-lg">{['🧄','🧅','🧈','🥚','🍅','🧂','🌶️','🫙','🍳','🥄'][i%10]}</span>
                      <span className="flex-1 text-sm font-medium text-foreground capitalize">{item}</span>
                      <button onClick={() => removeItem(i)}
                        className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            )}

            {error && <p className="text-sm text-destructive font-medium">⚠ {error}</p>}
            {cgError && <p className="text-sm text-destructive font-medium">⚠ {cgError}</p>}

            {/* ── ChefGPT Quick Recipe Card ── */}
            <AnimatePresence>
            {cgOpen && cgRecipe && (
              <motion.div
                key="cg-recipe"
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                className="bg-card border border-primary/30 rounded-2xl overflow-hidden shadow-sm"
              >
                {/* Header */}
                <div className="bg-primary/8 px-4 py-3 border-b border-primary/20 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-primary uppercase tracking-wider">ChefGPT Quick Recipe</p>
                    <p className="font-bold text-foreground text-sm truncate">{cgRecipe.name}</p>
                  </div>
                  <button onClick={() => setCgOpen(false)}
                    className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-destructive/10 hover:text-destructive shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </div>

                {/* Hero image */}
                {cgRecipe.image_url && (
                  <img src={cgRecipe.image_url} alt={cgRecipe.name}
                    className="w-full h-40 object-cover"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}

                <div className="p-4 space-y-3">
                  {/* Meta row */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                    {cgRecipe.servings && <span>👤 {cgRecipe.servings} servings</span>}
                    {cgRecipe.prep_time && <span>⏱ Prep: {cgRecipe.prep_time}</span>}
                    {cgRecipe.cook_time && <span>🔥 Cook: {cgRecipe.cook_time}</span>}
                  </div>

                  {cgRecipe.description && (
                    <p className="text-sm text-muted-foreground leading-relaxed">{cgRecipe.description}</p>
                  )}

                  {/* Ingredients */}
                  {cgRecipe.ingredients && cgRecipe.ingredients.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Ingredients</p>
                      <div className="bg-muted/40 rounded-xl overflow-hidden">
                        {cgRecipe.ingredients.map((ing, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 border-b border-border/20 last:border-0 text-sm">
                            <span className="text-foreground flex-1">{ing.name}</span>
                            {(ing.quantity || ing.unit) && (
                              <span className="text-muted-foreground text-xs shrink-0">
                                {[ing.quantity, ing.unit].filter(Boolean).join(' ')}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Instructions — collapsible */}
                  {cgRecipe.instructions && cgRecipe.instructions.length > 0 && (
                    <div>
                      <button
                        onClick={() => setCgStepsOpen(v => !v)}
                        className="flex items-center gap-2 w-full text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 hover:text-foreground transition-colors">
                        <ChefHat className="w-3.5 h-3.5 text-primary" />
                        Steps ({cgRecipe.instructions.length})
                        {cgStepsOpen ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
                      </button>
                      <AnimatePresence>
                      {cgStepsOpen && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }} className="overflow-hidden space-y-2">
                          {cgRecipe.instructions.map((step, i) => (
                            <div key={i} className="flex gap-3">
                              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                                {i + 1}
                              </div>
                              <p className="text-sm text-foreground leading-relaxed">{step}</p>
                            </div>
                          ))}
                        </motion.div>
                      )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* ── RECEIPTS TAB ── */}
        {tab === 'receipts' && (
          <motion.div key="receipts" initial={{opacity:0,x:10}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-10}} className="space-y-5">

            {/* Weekly spending widget */}
            <div className="bg-card border border-border rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-primary" />
                  <span className="font-bold text-foreground">This Week's Spending</span>
                </div>
                <span className="text-2xl font-black text-primary">${weekly.toFixed(2)}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-primary rounded-full h-2 transition-all"
                  style={{ width: `${Math.min(100, (weekly / 300) * 100)}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Based on {receipts.filter(r => new Date(r.scanned_at ?? 0).getTime() > Date.now() - 7*24*60*60*1000).length} receipt{receipts.length !== 1 ? 's' : ''} scanned this week
              </p>
            </div>

            {/* Scan button */}
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
              className="hidden" onChange={handleFileChange} />

            {scanSuccess && (
              <motion.div initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}}
                className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-foreground">Receipt saved!</p>
                  <p className="text-xs text-muted-foreground">Items added to your pantry automatically.</p>
                </div>
              </motion.div>
            )}

            {/* Scan preview + confirmation */}
            {(scanPreview || scanning) && !pendingScan && (
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {scanPreview && (
                  <img src={scanPreview} alt="Receipt" className="w-full max-h-48 object-contain bg-black/5" />
                )}
                <div className="p-4 flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-foreground">Reading your receipt…</p>
                    <p className="text-xs text-muted-foreground">Gemini AI is extracting all items</p>
                  </div>
                </div>
              </div>
            )}

            {/* Pending scan confirmation */}
            {pendingScan && (
              <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}
                className="bg-card border border-primary/30 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-border/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-foreground">{pendingScan.store_name}</p>
                      <p className="text-xs text-muted-foreground">{pendingScan.items.length} items found</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-black text-primary">${pendingScan.total_spent.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">total</p>
                    </div>
                  </div>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {pendingScan.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/20 last:border-0">
                      <span className="text-base shrink-0">{CAT_EMOJI[item.category] ?? '🛒'}</span>
                      <span className="flex-1 text-sm text-foreground capitalize">{item.name}</span>
                      <span className="text-sm font-bold text-primary shrink-0">${item.price.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="p-4 flex gap-2">
                  <button onClick={discardScan}
                    className="flex-1 h-11 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors">
                    Discard
                  </button>
                  <button onClick={confirmScan}
                    className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
                    <ShoppingCart className="w-4 h-4" /> Save & Stock Pantry
                  </button>
                </div>
              </motion.div>
            )}

            {scanError && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-2xl p-4">
                <p className="text-sm font-bold text-destructive mb-1">Scan failed</p>
                <p className="text-xs text-muted-foreground">{scanError}</p>
              </div>
            )}

            {/* Past receipts */}
            {receipts.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Scanned Receipts ({receipts.length})
                </p>
                <div className="space-y-2">
                  {receipts.map((r, i) => (
                    <div key={i} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Receipt className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-foreground truncate">{r.store_name || 'Unknown Store'}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.items.length} items · {r.scanned_at ? new Date(r.scanned_at).toLocaleDateString('en-CA', {month:'short',day:'numeric'}) : 'Today'}
                        </p>
                      </div>
                      <span className="text-base font-black text-primary shrink-0">${(r.total_spent??0).toFixed(2)}</span>
                      <button onClick={() => deleteReceipt(i)}
                        className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors shrink-0">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {receipts.length === 0 && !scanning && !pendingScan && (
              <div className="text-center py-12">
                <Receipt className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm font-bold text-foreground mb-1">No receipts yet</p>
                <p className="text-xs text-muted-foreground">Scan your grocery receipt to track spending and auto-stock your pantry</p>
              </div>
            )}
          </motion.div>
        )}
        </AnimatePresence>
      </div>

      {/* Fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur border-t border-border/50 p-4">
        <div className="container mx-auto max-w-xl">
          {tab === 'receipts' ? (
            <button onClick={() => fileInputRef.current?.click()} disabled={scanning}
              className="w-full h-14 rounded-2xl bg-amber-500 text-white font-bold text-base flex items-center justify-center gap-2.5 shadow-xl shadow-amber-500/30 hover:opacity-90 active:scale-95 disabled:opacity-50 transition-all">
              {scanning
                ? <><Loader2 className="w-5 h-5 animate-spin" /> Scanning receipt…</>
                : <><Camera className="w-5 h-5" /> Scan Receipt</>}
            </button>
          ) : (
            <AnimatePresence mode="wait">
              {generated ? (
                <motion.div key="done" initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}}
                  className="w-full h-14 rounded-2xl bg-green-500 text-white font-bold text-base flex items-center justify-center gap-2">
                  ✓ Meals generated! Taking you there…
                </motion.div>
              ) : (
                <motion.div key="gen" initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} className="flex gap-2">
                  {/* Quick Recipe from ChefGPT */}
                  <button
                    onClick={fetchChefGPTRecipe}
                    disabled={cgLoading || items.length < 2}
                    className="h-14 px-4 rounded-2xl bg-primary/15 text-primary font-bold text-sm flex items-center justify-center gap-1.5 border border-primary/30 hover:bg-primary/25 active:scale-95 disabled:opacity-40 transition-all shrink-0">
                    {cgLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Zap className="w-4 h-4" />}
                    Quick
                  </button>
                  {/* Full meal plan via Claude */}
                  <button
                    onClick={generateMeals} disabled={loading || items.length < 3}
                    className="flex-1 h-14 rounded-2xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 shadow-xl shadow-primary/30 hover:opacity-90 active:scale-95 disabled:opacity-50 transition-all">
                    {loading
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                      : <><Sparkles className="w-4 h-4" /> Full Meal Plan</>}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          )}
          {tab === 'pantry' && items.length < 3 && !loading && !cgLoading && (
            <p className="text-center text-xs text-muted-foreground mt-2">
              {items.length < 2 ? 'Add 2+ items for Quick Recipe · 3+ for Full Plan' : 'Add 1 more for Full Meal Plan'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
